import { etiquetaMotivo, etiquetaRama } from '@/lib/llamadas/presentacion';
import {
  ETIQUETA_DESENLACE,
  SIN_FILTROS,
  hayFiltrosActivos,
  type FiltroDesenlace,
  type FiltrosCuadro as Filtros,
  type OpcionesFiltro,
} from '@/lib/cuadro/filtros';
import { SelectorCliente } from './SelectorCliente';

// Chips de filtro del cuadro (change-001). Solo se pintan los valores presentes
// en el periodo cargado; un chip activo se pulsa otra vez para quitarlo.

const DESENLACES = (Object.keys(ETIQUETA_DESENLACE) as FiltroDesenlace[]).map((valor) => ({
  valor,
  etiqueta: ETIQUETA_DESENLACE[valor],
}));

function Chip({
  activo,
  onClick,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onClick}
      className={
        'rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors cursor-pointer focus-visible:outline-none focus-visible:shadow-[var(--ring)] ' +
        (activo
          ? 'border-[var(--orange)] bg-[var(--orange)] text-white'
          : 'border-[var(--border)] bg-[var(--surface)] text-[var(--navy)] hover:bg-[var(--surface-2)]')
      }
    >
      {children}
    </button>
  );
}

function Grupo({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11.5px] font-medium">{titulo}</span>
      {children}
    </div>
  );
}

export function FiltrosCuadro({
  filtros,
  opciones,
  onCambiar,
}: {
  filtros: Filtros;
  opciones: OpcionesFiltro;
  onCambiar: (f: Filtros) => void;
}) {
  // Pulsar el chip activo lo desactiva (vuelve a "todas").
  const alterna = <K extends keyof Filtros>(clave: K, valor: Filtros[K]) =>
    onCambiar({ ...filtros, [clave]: filtros[clave] === valor ? null : valor });

  // El desenlace siempre se ofrece, así que "nada que filtrar" solo ocurre
  // cuando no hay ni ramas, ni motivos, ni clientes: es decir, periodo vacío.
  const sinNadaQueFiltrar =
    opciones.ramas.length === 0 &&
    opciones.motivos.length === 0 &&
    opciones.clientes.length === 0 &&
    opciones.sinCliente === 0;
  if (sinNadaQueFiltrar) return null;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2" aria-label="Filtros">
      {opciones.ramas.length > 1 && (
        <Grupo titulo="Rama">
          {opciones.ramas.map((r) => (
            <Chip key={r} activo={filtros.rama === r} onClick={() => alterna('rama', r)}>
              {etiquetaRama(r)}
            </Chip>
          ))}
        </Grupo>
      )}

      {opciones.motivos.length > 1 && (
        <Grupo titulo="Motivo">
          {opciones.motivos.map((m) => (
            <Chip key={m} activo={filtros.motivo === m} onClick={() => alterna('motivo', m)}>
              {etiquetaMotivo(m)}
            </Chip>
          ))}
        </Grupo>
      )}

      <Grupo titulo="Desenlace">
        {DESENLACES.map((d) => (
          <Chip
            key={d.valor}
            activo={filtros.desenlace === d.valor}
            onClick={() => alterna('desenlace', d.valor)}
          >
            {d.etiqueta}
          </Chip>
        ))}
      </Grupo>

      <SelectorCliente
        valor={filtros.cliente}
        clientes={opciones.clientes}
        sinCliente={opciones.sinCliente}
        onCambiar={(cliente) => onCambiar({ ...filtros, cliente })}
      />

      {hayFiltrosActivos(filtros) && (
        <button
          type="button"
          onClick={() => onCambiar(SIN_FILTROS)}
          className="text-[12px] font-medium text-[var(--orange)] underline underline-offset-2 cursor-pointer focus-visible:outline-none focus-visible:shadow-[var(--ring)]"
        >
          Limpiar filtros
        </button>
      )}
    </div>
  );
}
